import {
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  Assignment as AssignIcon,
  AssignmentReturn as UnassignIcon,
  ToggleOn as ToggleOnIcon,
  Block as BlockIcon,
  Security as SecurityIcon,
  Lock as LockIcon,
  Business as BusinessIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  BadgeOutlined as BadgeIcon,
} from '@mui/icons-material';

export const MFA_TYPE = Object.freeze({
  SOFTWARE_TOKEN_MFA: 'SOFTWARE_TOKEN_MFA',
  EMAIL_OTP: 'EMAIL_OTP',
  SMS_MFA: 'SMS_MFA',
  NONE: 'NONE',
});

export const PERMISSION_TYPE = Object.freeze({
  ROLE: 'role',
  PERMISSION: 'permission',
});

export const USER_TYPE = Object.freeze({
  ADMIN: 'admin',
  FACILITY: 'facility',
  SFTP: 'sftp',
  SYSTEM: 'system',
});

export const USER_STATUS = Object.freeze({
  INACTIVE: 0,
  ACTIVE: 1,
  ONBOARDING: 2,
  DELETED: 3,
});

export const USER_HISTORY_ACTION = Object.freeze({
  CREATE: 'CREATE',
  USER_CREATED: 'USER_CREATED',
  UPDATE: 'UPDATE',
  METADATA_CHANGED: 'METADATA_CHANGED',
  ROLE_ASSIGN: 'ROLE_ASSIGN',
  ROLE_UNASSIGN: 'ROLE_UNASSIGN',
  ROLE_CHANGED: 'ROLE_CHANGED',
  ROLE_CREATED: 'ROLE_CREATED',
  ROLE_UPDATED: 'ROLE_UPDATED',
  ROLE_DELETED: 'ROLE_DELETED',
  PERMISSIONS_TYPE_CHANGE: 'PERMISSIONS_TYPE_CHANGE',
  PERMISSIONS_UPDATE: 'PERMISSIONS_UPDATE',
  PERMISSIONS_CHANGED: 'PERMISSIONS_CHANGED',
  STATUS_CHANGE: 'STATUS_CHANGE',
  ENABLE: 'ENABLE',
  DISABLE: 'DISABLE',
  DELETE: 'DELETE',
  MFA_CHANGED: 'MFA_CHANGED',
  FACILITY_ACCESS_CHANGED: 'FACILITY_ACCESS_CHANGED',
  EMPLOYEE_INFO_CHANGED: 'EMPLOYEE_INFO_CHANGED',
});

const USER_TYPE_LABELS = {
  [USER_TYPE.ADMIN]: 'Admin',
  [USER_TYPE.FACILITY]: 'Facility User',
  [USER_TYPE.SFTP]: 'SFTP',
  [USER_TYPE.SYSTEM]: 'System',
};

const MFA_TYPE_LABELS = {
  [MFA_TYPE.SOFTWARE_TOKEN_MFA]: 'Authenticator App',
  [MFA_TYPE.EMAIL_OTP]: 'Email OTP',
  [MFA_TYPE.SMS_MFA]: 'SMS',
  [MFA_TYPE.NONE]: 'None',
};

const ACTION_TITLES = {
  [USER_HISTORY_ACTION.CREATE]: 'User Created',
  [USER_HISTORY_ACTION.USER_CREATED]: 'User Created',
  [USER_HISTORY_ACTION.UPDATE]: 'User Updated',
  [USER_HISTORY_ACTION.METADATA_CHANGED]: 'User Updated',
  [USER_HISTORY_ACTION.ROLE_ASSIGN]: 'Role Assigned',
  [USER_HISTORY_ACTION.ROLE_UNASSIGN]: 'Role Removed',
  [USER_HISTORY_ACTION.PERMISSIONS_TYPE_CHANGE]: 'Permission Type Changed',
  [USER_HISTORY_ACTION.STATUS_CHANGE]: 'User Status Changed',
  [USER_HISTORY_ACTION.PERMISSIONS_UPDATE]: 'Permissions Updated',
  [USER_HISTORY_ACTION.PERMISSIONS_CHANGED]: 'Permissions Updated',
  [USER_HISTORY_ACTION.DELETE]: 'User Deleted',
  [USER_HISTORY_ACTION.ENABLE]: 'User Enabled',
  [USER_HISTORY_ACTION.DISABLE]: 'User Disabled',
  [USER_HISTORY_ACTION.ROLE_CHANGED]: 'Role Changed',
  [USER_HISTORY_ACTION.MFA_CHANGED]: 'MFA Settings Changed',
  [USER_HISTORY_ACTION.FACILITY_ACCESS_CHANGED]: 'Facility Access Changed',
  [USER_HISTORY_ACTION.ROLE_CREATED]: 'Role Created',
  [USER_HISTORY_ACTION.ROLE_UPDATED]: 'Role Updated',
  [USER_HISTORY_ACTION.ROLE_DELETED]: 'Role Deleted',
  [USER_HISTORY_ACTION.EMPLOYEE_INFO_CHANGED]: 'Employee Info Updated',
};

const ACTION_ICON_CONFIG = {
  [USER_HISTORY_ACTION.CREATE]: { Icon: PersonAddIcon, color: 'success' },
  [USER_HISTORY_ACTION.USER_CREATED]: { Icon: PersonAddIcon, color: 'success' },
  [USER_HISTORY_ACTION.UPDATE]: { Icon: EditIcon, color: 'warning' },
  [USER_HISTORY_ACTION.METADATA_CHANGED]: { Icon: EditIcon, color: 'warning' },
  [USER_HISTORY_ACTION.ROLE_ASSIGN]: { Icon: AssignIcon, color: 'info' },
  [USER_HISTORY_ACTION.ROLE_UNASSIGN]: {
    Icon: UnassignIcon,
    color: 'secondary',
  },
  [USER_HISTORY_ACTION.PERMISSIONS_TYPE_CHANGE]: {
    Icon: ToggleOnIcon,
    color: 'primary',
  },
  [USER_HISTORY_ACTION.STATUS_CHANGE]: { Icon: BlockIcon, color: 'error' },
  [USER_HISTORY_ACTION.PERMISSIONS_UPDATE]: {
    Icon: SecurityIcon,
    color: 'warning',
  },
  [USER_HISTORY_ACTION.PERMISSIONS_CHANGED]: {
    Icon: SecurityIcon,
    color: 'warning',
  },
  [USER_HISTORY_ACTION.ENABLE]: { Icon: ToggleOnIcon, color: 'success' },
  [USER_HISTORY_ACTION.DISABLE]: { Icon: BlockIcon, color: 'error' },
  [USER_HISTORY_ACTION.ROLE_CHANGED]: { Icon: AssignIcon, color: 'info' },
  [USER_HISTORY_ACTION.MFA_CHANGED]: { Icon: LockIcon, color: 'info' },
  [USER_HISTORY_ACTION.FACILITY_ACCESS_CHANGED]: {
    Icon: BusinessIcon,
    color: 'primary',
  },
  [USER_HISTORY_ACTION.ROLE_CREATED]: { Icon: AddIcon, color: 'success' },
  [USER_HISTORY_ACTION.ROLE_UPDATED]: { Icon: EditIcon, color: 'warning' },
  [USER_HISTORY_ACTION.ROLE_DELETED]: { Icon: DeleteIcon, color: 'error' },
  [USER_HISTORY_ACTION.DELETE]: { Icon: DeleteIcon, color: 'error' },
  [USER_HISTORY_ACTION.EMPLOYEE_INFO_CHANGED]: {
    Icon: BadgeIcon,
    color: 'info',
  },
};

export const getUserTypeLabel = (userType) =>
  USER_TYPE_LABELS[userType] ?? null;

export const getMfaTypeLabel = (mfaType) =>
  MFA_TYPE_LABELS[mfaType] ?? (mfaType == null ? 'None' : mfaType);

export const getActionTitle = (action) =>
  ACTION_TITLES[action] ??
  action.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

export const getTimelineIconAndColor = (action) => {
  const config = ACTION_ICON_CONFIG[action];
  if (config) {
    return config;
  }
  return { Icon: EditIcon, color: 'grey' };
};

export const getActionDescription = (historyItem) => {
  const { action, previous_value, new_value, metadata } = historyItem;

  switch (action) {
    case USER_HISTORY_ACTION.CREATE:
    case USER_HISTORY_ACTION.USER_CREATED:
      return `User account created${
        new_value?.email ? ` for ${new_value.email}` : ''
      }`;
    case USER_HISTORY_ACTION.UPDATE:
    case USER_HISTORY_ACTION.METADATA_CHANGED:
      if (previous_value?.name !== new_value?.name) {
        return `Name changed from "${previous_value?.name}" to "${new_value?.name}"`;
      }
      if (previous_value?.email !== new_value?.email) {
        return `Email changed from "${previous_value?.email}" to "${new_value?.email}"`;
      }
      if (previous_value?.organization_id !== new_value?.organization_id) {
        return 'Organization assignment was updated';
      }
      return 'User information was updated';
    case USER_HISTORY_ACTION.ROLE_ASSIGN:
      return `Role "${
        metadata?.role_name || new_value?.role_name || 'Unknown'
      }" assigned to user`;
    case USER_HISTORY_ACTION.ROLE_UNASSIGN:
      return `Role "${
        metadata?.role_name || previous_value?.role_name || 'Unknown'
      }" removed from user`;
    case USER_HISTORY_ACTION.PERMISSIONS_TYPE_CHANGE: {
      const fromType =
        previous_value?.permissions_type === PERMISSION_TYPE.ROLE
          ? 'role-based'
          : 'direct';
      const toType =
        new_value?.permissions_type === PERMISSION_TYPE.ROLE
          ? 'role-based'
          : 'direct';
      return `Switched from ${fromType} to ${toType} permissions`;
    }
    case USER_HISTORY_ACTION.STATUS_CHANGE: {
      const statusText = new_value?.status === 1 ? 'activated' : 'deactivated';
      return `User account ${statusText}`;
    }
    case USER_HISTORY_ACTION.PERMISSIONS_UPDATE:
    case USER_HISTORY_ACTION.PERMISSIONS_CHANGED:
      return metadata?.reason || 'User permissions were updated';
    case USER_HISTORY_ACTION.DELETE:
      return 'User account was deleted';
    case USER_HISTORY_ACTION.ENABLE:
      return 'User account enabled';
    case USER_HISTORY_ACTION.DISABLE:
      return 'User account disabled';
    case USER_HISTORY_ACTION.ROLE_CHANGED: {
      const fromRole =
        historyItem.previous_role_name ||
        previous_value?.role_name ||
        previous_value?.name;
      const toRole =
        historyItem.new_role_name || new_value?.role_name || new_value?.name;
      if (fromRole && toRole) {
        return `Role changed from "${fromRole}" to "${toRole}"`;
      }
      if (toRole) {
        return `Role set to "${toRole}"`;
      }
      return metadata?.reason || 'User role was changed';
    }
    case USER_HISTORY_ACTION.MFA_CHANGED: {
      const fromMfa = previous_value?.mfa_type ?? previous_value?.type;
      const toMfa = new_value?.mfa_type ?? new_value?.type;
      if (fromMfa !== undefined && toMfa !== undefined) {
        return `MFA changed from "${getMfaTypeLabel(
          fromMfa,
        )}" to "${getMfaTypeLabel(toMfa)}"`;
      }
      return 'MFA settings were updated';
    }
    case USER_HISTORY_ACTION.FACILITY_ACCESS_CHANGED:
      return metadata?.reason || 'Facility access was updated';
    case USER_HISTORY_ACTION.ROLE_CREATED:
      return `Role "${
        new_value?.name || metadata?.role_name || 'Unknown'
      }" was created`;
    case USER_HISTORY_ACTION.ROLE_UPDATED:
      return `Role "${
        new_value?.name || metadata?.role_name || 'Unknown'
      }" was updated`;
    case USER_HISTORY_ACTION.ROLE_DELETED:
      return `Role "${
        previous_value?.name || metadata?.role_name || 'Unknown'
      }" was deleted`;
    case USER_HISTORY_ACTION.EMPLOYEE_INFO_CHANGED:
      return 'Employee information was updated';
    default:
      return `User ${action.toLowerCase().replace(/_/g, ' ')}`;
  }
};

export const getShortChanges = (historyItem) => {
  const { action, previous_value, new_value, metadata } = historyItem;

  switch (action) {
    case USER_HISTORY_ACTION.CREATE:
    case USER_HISTORY_ACTION.USER_CREATED:
      return new_value?.email || 'New user account';
    case USER_HISTORY_ACTION.UPDATE:
    case USER_HISTORY_ACTION.METADATA_CHANGED:
      if (previous_value?.name !== new_value?.name) {
        return `Name: ${new_value?.name}`;
      }
      if (previous_value?.email !== new_value?.email) {
        return `Email: ${new_value?.email}`;
      }
      if (previous_value?.organization_id !== new_value?.organization_id) {
        return 'Organization changed';
      }
      return 'Profile updated';
    case USER_HISTORY_ACTION.ROLE_ASSIGN:
      return metadata?.role_name || new_value?.role_name || 'Role assigned';
    case USER_HISTORY_ACTION.ROLE_UNASSIGN:
      return metadata?.role_name || previous_value?.role_name || 'Role removed';
    case USER_HISTORY_ACTION.PERMISSIONS_TYPE_CHANGE: {
      const toType =
        new_value?.permissions_type === PERMISSION_TYPE.ROLE
          ? 'Role-based'
          : 'Direct';
      return `→ ${toType}`;
    }
    case USER_HISTORY_ACTION.STATUS_CHANGE: {
      const status = new_value?.status === 1 ? 'Active' : 'Inactive';
      return status;
    }
    case USER_HISTORY_ACTION.PERMISSIONS_UPDATE:
    case USER_HISTORY_ACTION.PERMISSIONS_CHANGED:
      return 'Permissions modified';
    case USER_HISTORY_ACTION.DELETE:
      return 'Account deleted';
    case USER_HISTORY_ACTION.ENABLE:
      return 'Active';
    case USER_HISTORY_ACTION.DISABLE:
      return 'Inactive';
    case USER_HISTORY_ACTION.ROLE_CHANGED: {
      const fromRole = historyItem.previous_role_name;
      const toRole =
        historyItem.new_role_name || new_value?.role_name || new_value?.name;
      if (fromRole && toRole) {
        return `${fromRole} → ${toRole}`;
      }
      return toRole || 'Role changed';
    }
    case USER_HISTORY_ACTION.MFA_CHANGED: {
      const mfaType = new_value?.mfa_type ?? new_value?.type;
      return mfaType !== undefined ? getMfaTypeLabel(mfaType) : 'MFA updated';
    }
    case USER_HISTORY_ACTION.FACILITY_ACCESS_CHANGED:
      return 'Access updated';
    case USER_HISTORY_ACTION.ROLE_CREATED:
      return new_value?.name || metadata?.role_name || 'New role';
    case USER_HISTORY_ACTION.ROLE_UPDATED:
      return new_value?.name || metadata?.role_name || 'Role modified';
    case USER_HISTORY_ACTION.ROLE_DELETED:
      return previous_value?.name || metadata?.role_name || 'Role removed';
    case USER_HISTORY_ACTION.EMPLOYEE_INFO_CHANGED:
      return 'Employee info updated';
    default:
      return null;
  }
};
